import { User } from "@/common/types";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SIGN_OUT_MUTATION } from "../../../graphql/mutations/signOut";
interface AuthState {
  user: Pick<User, "id" | "name" | "email" | "role" | "companyId" | "isAdmin"> | null;
  // Set the moment Apollo sees an UNAUTHENTICATED response. Read by
  // ProtectedRoute to immediately unmount the page tree so error UI from
  // the failing query can't render in the gap before redirect.
  sessionLost: boolean;
}

const initialState: AuthState = {
  user: null,
  sessionLost: false,
};

export const signOutThunk = createAsyncThunk(
  "auth/signOut",
  async (_, { dispatch }) => {
    try {
      const { default: client } = await import("@/common/apollo/apolloClient");
      await client.mutate({ mutation: SIGN_OUT_MUTATION });
      dispatch(signOutState()); // Clear local state after API call succeeds
    } catch (error) {
      console.error("Error during sign out:", error);
      dispatch(signOutState()); // Ensure sign-out even if mutation fails
    } finally {
      // Wipe the persisted snapshot so a refresh after sign-out cannot
      // resurrect the previous user. Dynamic import avoids a circular
      // dependency between authSlice and the store module.
      try {
        const { persistor } = await import("@/store/store");
        await persistor.purge();
      } catch (purgeErr) {
        console.error("Failed to purge persisted state:", purgeErr);
      }
      // Drop the Apollo cache so the next user to sign in on this device
      // cannot see the previous user's cached queries. clearStore() (not
      // resetStore) avoids refetching active queries during teardown.
      try {
        const { default: client, apolloPersistor } = await import("@/common/apollo/apolloClient");
        await client.clearStore();
        // clearStore() only empties the in-memory cache. The persisted copy in
        // localStorage will resurrect the previous user's query results on the
        // next page load unless explicitly purged.
        if (apolloPersistor) {
          await apolloPersistor.purge();
        }
      } catch (clearErr) {
        console.error("Failed to clear Apollo cache:", clearErr);
      }
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<Pick<User, "id" | "name" | "email" | "role" | "companyId" | "isAdmin">>) => {
      state.user = action.payload;
      state.sessionLost = false;
    },
    setSessionLost(state) {
      state.sessionLost = true;
    },
    signOutState(state) {
      state.user = null;
      state.sessionLost = true;
      localStorage.removeItem("accessToken");
      // Clear server-set cookie via API (fire-and-forget)
      if (typeof window !== "undefined") {
        fetch("/api/auth/clear-cookie", { method: "POST" }).catch(() => {});
      }
    },
  },
});

export const { setUser, setSessionLost, signOutState } = authSlice.actions;
export default authSlice.reducer;
